import React, { useContext, useState, useEffect } from "react";
import {
  Modal,
  Button,
  Typography,
  Box,
  CircularProgress,
  Avatar,
} from "@mui/material";
import { styled, useTheme, useMediaQuery } from "@mui/system";
import { motion } from "framer-motion";
import GlobalStateContext from "../context/globalStateContext";
import UserContext from "../context/userContext";
import { COLORS } from "../utils/COLORS";
import { functions, firestore } from "../firebaseConfig";
import { httpsCallable } from "firebase/functions";
import { toast } from "react-simple-toasts";
import * as amplitude from '@amplitude/analytics-browser';

const StyledModal = styled(Modal)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-end",
  justifyContent: "center",
}));

const ModalContent = styled(Box)(({ theme }) => ({
  backgroundColor: "#0F172A",
  borderRadius: "14px",
  boxShadow: theme.shadows[5],
  padding: "32px 24px",
  outline: "none",
  width: "90%",
  maxWidth: "420px",
  maxHeight: "80vh",
  overflowY: "auto",
  textAlign: "center",
  border: "1px solid #1E293B",
}));

const DogInfoCard = styled(Box)({
  backgroundColor: "#1F2937",
  borderRadius: "8px",
  padding: "16px",
  marginBottom: "24px",
  textAlign: "left",
});

const AmountDisplay = styled(Box)({
  backgroundColor: "#1F2937",
  borderRadius: "8px",
  padding: "16px",
  marginBottom: "16px",
});

const ConfirmButton = styled(Button)({
  width: "100%",
  backgroundColor: "#10B981",
  color: "#FFFFFF",
  borderRadius: "10px",
  padding: "12px",
  marginBottom: "14px",
  fontSize: "15px",
  fontWeight: "600",
  textTransform: "none",
  boxShadow: "0 2px 8px rgba(16, 185, 129, 0.25)",
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: "#059669",
    transform: "translateY(-1px)",
    boxShadow: "0 4px 12px rgba(16, 185, 129, 0.35)",
  },
});

const ConfirmPledgeModal = ({ selectedAmount }) => {
  const { user } = useContext(UserContext);
  const { globalState, setGlobalState } = useContext(GlobalStateContext);
  const [isProcessing, setIsProcessing] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState("");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const handleClose = () => {
    setGlobalState({ ...globalState, showConfirmPledgeModal: false });
  };

  const totalAmount = selectedAmount;

  const handleConfirmPledge = async () => {
    setIsProcessing(true);

    try {
      const createCustomerForDelayedCharge = httpsCallable(
        functions,
        "createCustomerForDelayedCharge"
      );

      const result = await createCustomerForDelayedCharge({
        email: user.email,
        amount: totalAmount * 100, // Convert to cents
        paymentMethodId: user.stripePaymentMethodId,
        dogSlug: globalState.selectedDog?.slug,
        orderDetails: {
          dogName: globalState.selectedDog?.name,
          pledgeAmount: selectedAmount,
          tipAmount: 0,
          totalAmount: totalAmount,
        },
      });

      const { success, orderId, orderData } = result.data;

      if (success) {
        // Save to flat Firestore pledges collection
        await firestore
          .collection(`pledges`)
          .doc(orderId)
          .set({
            ...orderData,
            userId: user.uid,
            userEmail: user.email,
            userName: user.username,
            displayAmount: selectedAmount,
            tipAmount: 0,
            totalAmount: totalAmount,
            createdAt: new Date().toISOString(),
            status: "requires_capture",
            slug: globalState.selectedDog?.slug,
            shelterId: globalState.selectedDog?.shelterId,
            name: globalState.selectedDog?.name,
            breed: globalState.selectedDog?.breed,
            age: globalState.selectedDog?.age,
            animalId: globalState.selectedDog?.animalId,
          });

        toast("Pledge successful!");

        // Track pledge event with Amplitude
        amplitude.track('pledge', {
          dogName: globalState.selectedDog?.name,
          dogSlug: globalState.selectedDog?.slug,
          pledgeAmount: selectedAmount,
          tipAmount: 0,
          totalAmount: totalAmount,
          userId: user.uid,
          userEmail: user.email
        });

        // Close ConfirmPledgeModal and open SubModal for subscription upsell
        setGlobalState({
          ...globalState,
          showConfirmPledgeModal: false,
          showSubModal: true,
        });
      } else {
        throw new Error("Failed to create pledge");
      }
    } catch (err) {
      toast("Pledge failed: " + err.message);
      console.error("Pledge error:", err);
    } finally {
      setIsProcessing(false);
    }
  };

  // Live countdown timer
  useEffect(() => {
    const dog = globalState.selectedDog;
    if (!dog?.euthanasiaDate) return;

    const updateCountdown = () => {
      const now = new Date();
      const today = now.toISOString().split('T')[0];
      const isToday = dog.euthanasiaDate === today;

      // If today, use shelter closing time; otherwise end of day
      let deadline = "T23:59:59";
      if (isToday && dog.shelter?.closingTime) {
        const closeTime = dog.shelter.closingTime.toString().padStart(4, "0");
        const hours = closeTime.slice(0, 2);
        const minutes = closeTime.slice(2, 4);
        deadline = `T${hours}:${minutes}:00`;
      }

      const euthanasiaDate = new Date(dog.euthanasiaDate + deadline);
      const difference = euthanasiaDate.getTime() - now.getTime();
      const pad = (n) => n.toString().padStart(2, "0");

      if (difference <= 0) {
        setTimeRemaining("EXPIRED");
      } else {
        const totalHours = Math.floor(difference / (1000 * 60 * 60));
        const minutes = Math.floor((difference / (1000 * 60)) % 60);
        const seconds = Math.floor((difference / 1000) % 60);
        setTimeRemaining(`${pad(totalHours)}:${pad(minutes)}:${pad(seconds)}`);
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [globalState.selectedDog]);

  return (
    <StyledModal
      open={globalState.showConfirmPledgeModal}
      onClose={handleClose}
      BackdropProps={{
        style: { backgroundColor: "rgba(0, 0, 0, 0.5)" },
      }}
    >
      <motion.div
        initial={{ y: "100%" }}
        animate={{ y: 0 }}
        exit={{ y: "100%" }}
        onClick={handleClose}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: window.innerWidth > 600 ? "center" : "flex-end",
          height: "100%",
        }}
      >
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <Typography
            sx={{
              fontSize: "24px",
              fontWeight: "700",
              color: "#FFFFFF",
              marginBottom: "8px",
            }}
          >
            Confirm Your Pledge
          </Typography>

          {/* Dog Info Card */}
          <DogInfoCard>
            <Typography
              sx={{
                fontSize: "12px",
                color: "#10B981",
                fontWeight: "600",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                marginBottom: "8px",
              }}
            >
              Pledging to
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <Avatar
                src={globalState.selectedDog?.images?.[0] || globalState.selectedDog?.videos?.[0]}
                alt={globalState.selectedDog?.name}
                sx={{
                  width: "64px",
                  height: "64px",
                  borderRadius: "8px",
                }}
              />
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontSize: "18px",
                    color: "#FFFFFF",
                    fontWeight: "600",
                    marginBottom: "4px",
                  }}
                >
                  {globalState.selectedDog?.name}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: "#9CA3AF",
                    marginBottom: "4px",
                  }}
                >
                  {globalState.selectedDog?.breed} • {globalState.selectedDog?.age}
                </Typography>
                {timeRemaining && (
                  <Box
                    sx={{
                      display: "inline-block",
                      backgroundColor: "#991B1B",
                      padding: "4px 8px",
                      borderRadius: "4px",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "12px",
                        color: "#FFFFFF",
                        fontWeight: "700",
                        fontFamily: "monospace",
                        letterSpacing: "1px",
                      }}
                    >
                      ⏰ {timeRemaining} LEFT
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </DogInfoCard>

          {/* Amount Display */}
          <AmountDisplay>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "8px",
              }}
            >
              <Typography sx={{ fontSize: "14px", color: "#9CA3AF" }}>
                Pledge Amount
              </Typography>
              <Typography sx={{ fontSize: "14px", color: "#FFFFFF", fontWeight: "600" }}>
                ${selectedAmount}
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                paddingTop: "8px",
                borderTop: "1px solid #374151",
              }}
            >
              <Typography sx={{ fontSize: "16px", color: "#FFFFFF", fontWeight: "700" }}>
                Total
              </Typography>
              <Typography sx={{ fontSize: "16px", color: "#10B981", fontWeight: "700" }}>
                ${totalAmount}
              </Typography>
            </Box>
          </AmountDisplay>

          <Typography
            sx={{
              fontSize: "12px",
              color: "#9CA3AF",
              marginBottom: "16px",
              textAlign: "center",
              fontWeight:'bold'
            }}
          >
            Your card is ONLY charged if a rescue organization uploads verified proof of rescue. You will be notified via email.
          </Typography>

          <ConfirmButton
            onClick={handleConfirmPledge}
            disabled={isProcessing}
          >
            {isProcessing ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Confirm Pledge"
            )}
          </ConfirmButton>

          
        </ModalContent>
      </motion.div>
    </StyledModal>
  );
};

export default ConfirmPledgeModal;
